import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import {
  ContentHeader,
  Field,
  Modal,
  MoneyInput,
  PercentInput,
  QueryStatus,
  StatusBadge,
  useToast,
} from "@cx/ui";
import type { FiscalFileLink, ServiceInvoiceView } from "@cx/contracts";
import { formatBRL, formatCompetence, formatDocument, formatPct } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ContactPicker } from "../finance/ContactPicker";
import { ENVIRONMENT_LABEL } from "../invoices/labels";
import { ServiceFields } from "./ServiceFields";
import {
  NATURE_LABEL,
  NFSE_EVENT_STATUS_LABEL,
  NFSE_STATUS_LABEL,
  NFSE_STATUS_TONE,
  emptyNfseForm,
  isoDateTimeBR,
  nfseBody,
  nfseEventInFlight,
  nfseFormOf,
  nfseInFlight,
  nfseMessage,
  nfseTitle,
  type NfseForm,
} from "./labels";

/**
 * Serviços › NFS-e (RF-SER-04): rascunho (avulso ou da cobrança do contrato) editável até autorizar; Enviar, Consultar
 * de novo, Cancelar (evento, com motivo) e baixar XML/PDF. A tela consulta sozinha enquanto a nota ou o cancelamento
 * estão em envio.
 */
export function ServiceInvoicePage(): ReactElement {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const key = ["service-invoices", companyId, "view", id];
  const view = useQuery({
    queryKey: key,
    enabled: !!id,
    refetchOnMount: "always",
    queryFn: () => api<ServiceInvoiceView>(`/service-invoices/${id}`),
    refetchInterval: (query) => {
      const v = query.state.data;
      return v && (nfseInFlight(v.status) || v.events.some(nfseEventInFlight)) ? 2500 : false;
    },
  });
  const d = view.data;
  const [f, setF] = useState<NfseForm>(emptyNfseForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (d) setF(nfseFormOf(d));
  }, [d]);
  const set = <K extends keyof NfseForm>(k: K, v: NfseForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const refresh = () => qc.invalidateQueries({ queryKey: ["service-invoices", companyId] });
  const onError = (e: Error) => {
    toast("danger", errorMessage(e));
    void refresh();
  };

  const canCreate = can(perms, "invoices", "create");
  const canUpdate = can(perms, "invoices", "update");
  const editable = !id ? canCreate : !!d?.editable && canUpdate;

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify(nfseBody(f));
      return id
        ? api<ServiceInvoiceView>(`/service-invoices/${id}`, { method: "PUT", body })
        : api<ServiceInvoiceView>("/service-invoices", { method: "POST", body });
    },
    onSuccess: (v) => {
      setErrors({});
      toast("success", "NFS-e salva");
      void refresh();
      if (!id) navigate({ to: "/servicos/notas-de-servico/$id", params: { id: v.id } });
      else qc.setQueryData(key, v);
    },
    onError: (e) => {
      const fields = e instanceof ApiFailure && e.fields ? { ...e.fields } : {};
      if (e instanceof ApiFailure && e.code === "invalid_contact") fields.contactId = e.message;
      setErrors(fields);
      onError(e);
    },
  });
  const [issueKey, setIssueKey] = useState(() => crypto.randomUUID());
  const issue = useMutation({
    mutationFn: async () => {
      // Salva o que está na tela antes de enviar (o envio usa o rascunho gravado).
      await api(`/service-invoices/${id}`, { method: "PUT", body: JSON.stringify(nfseBody(f)) });
      return api<ServiceInvoiceView>(`/service-invoices/${id}/issue`, {
        method: "POST",
        headers: { "Idempotency-Key": issueKey },
      });
    },
    onSuccess: (v) => {
      setIssueKey(crypto.randomUUID());
      qc.setQueryData(key, v);
      toast("info", "NFS-e enviada à prefeitura");
      void refresh();
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      onError(e);
    },
  });
  const retry = useMutation({
    mutationFn: () => api<ServiceInvoiceView>(`/service-invoices/${id}/refresh`, { method: "POST" }),
    onSuccess: (v) => {
      qc.setQueryData(key, v);
      toast("info", "Consultando a prefeitura de novo");
    },
    onError,
  });
  const retryEvent = useMutation({
    mutationFn: (eventId: string) =>
      api<ServiceInvoiceView>(`/service-invoices/events/${eventId}/refresh`, { method: "POST" }),
    onSuccess: () => {
      toast("info", "Consultando o cancelamento de novo");
      void refresh();
    },
    onError,
  });
  const download = useMutation({
    mutationFn: (file: "pdf" | "xml") => api<FiscalFileLink>(`/service-invoices/${id}/${file}`),
    onSuccess: (r) => window.location.assign(r.url),
    onError,
  });
  const [canceling, setCanceling] = useState(false);
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [cancelKey, setCancelKey] = useState(() => crypto.randomUUID());
  const cancel = useMutation({
    mutationFn: () =>
      api<ServiceInvoiceView>(`/service-invoices/${id}/cancel`, {
        method: "POST",
        headers: { "Idempotency-Key": cancelKey },
        body: JSON.stringify({ reason, code: code.trim() || null }),
      }),
    onSuccess: (v) => {
      setCanceling(false);
      qc.setQueryData(key, v);
      toast("info", "Cancelamento enviado à prefeitura");
      void refresh();
    },
    onError,
  });

  const message = d ? (nfseMessage(d.rejectionCode, d.rejectionReason) ?? d.statusMessage) : null;
  const eventBusy = !!d?.events.some(nfseEventInFlight);
  const busy = save.isPending || issue.isPending;

  return (
    <>
      <ContentHeader
        title={d ? nfseTitle(d) : id ? "Nota de serviço" : "Nova NFS-e"}
        breadcrumb={["Serviços", "Notas de serviço", d?.nfseNumber ?? (id ? "" : "Nova")]}
        actions={
          <>
            <Link to="/servicos/notas-de-servico" className="btn btn-outline-secondary">
              Voltar
            </Link>
            {d?.hasPdf && (
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={download.isPending}
                onClick={() => download.mutate("pdf")}
              >
                Baixar PDF
              </button>
            )}
            {d?.hasXml && (
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={download.isPending}
                onClick={() => download.mutate("xml")}
              >
                Baixar XML
              </button>
            )}
            {d?.canRefresh && canUpdate && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={retry.isPending}
                onClick={() => retry.mutate()}
              >
                Consultar de novo
              </button>
            )}
            {d?.canCancel && canUpdate && (
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={eventBusy || cancel.isPending}
                onClick={() => {
                  setReason("");
                  setCode("");
                  setCancelKey(crypto.randomUUID());
                  setCanceling(true);
                }}
              >
                Cancelar NFS-e
              </button>
            )}
            {editable && (
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={busy}
                onClick={() => save.mutate()}
              >
                Salvar rascunho
              </button>
            )}
            {id && d?.editable && canCreate && canUpdate && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => issue.mutate()}
              >
                {d.status === "rejected" ? "Enviar de novo" : "Enviar NFS-e"}
              </button>
            )}
          </>
        }
      />
      <QueryStatus loading={!!id && view.isLoading} error={view.error} />
      {d && (
        <div className="box card p-3 mb-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span>Situação:</span>
            <StatusBadge tone={NFSE_STATUS_TONE[d.status]}>{NFSE_STATUS_LABEL[d.status]}</StatusBadge>
            <span className="text-muted small">{ENVIRONMENT_LABEL[d.environment]}</span>
            {d.origin?.type === "service_order" && (
              <span className="small">
                <Link to="/servicos/ordens-de-servico/$id" params={{ id: d.origin.id }}>
                  Ordem de serviço nº {d.origin.number}
                </Link>
              </span>
            )}
            {d.origin?.type === "contract_billing" && (
              <span className="small">
                Cobrança do{" "}
                <Link to="/servicos/contratos/$id" params={{ id: d.origin.contractId }}>
                  contrato nº {d.origin.contractNumber}
                </Link>{" "}
                — {formatCompetence(d.origin.competence)}
              </span>
            )}
          </div>
          {message && (
            <div
              className={`alert ${d.status === "rejected" ? "alert-danger" : "alert-info"} py-2 mt-2 mb-0`}
              data-testid="nfse-message"
            >
              {message}
            </div>
          )}
          {d.status === "authorized" || d.status === "canceled" ? (
            <dl className="row mb-0 mt-3">
              <Info label="Número da NFS-e">{d.nfseNumber}</Info>
              <Info label="Código de verificação">{d.verificationCode}</Info>
              <Info label="Autorizada em">{d.authorizedAt && isoDateTimeBR(d.authorizedAt)}</Info>
              {d.canceledAt && <Info label="Cancelada em">{isoDateTimeBR(d.canceledAt)}</Info>}
              {d.rpsNumber && (
                <Info label="RPS">
                  {d.rpsNumber}
                  {d.rpsSeries && ` (série ${d.rpsSeries})`}
                </Info>
              )}
              {d.linkUrl && (
                <Info label="Consulta pública">
                  <a href={d.linkUrl} target="_blank" rel="noopener noreferrer">
                    Abrir na prefeitura
                  </a>
                </Info>
              )}
            </dl>
          ) : null}
        </div>
      )}
      {(!id || d) &&
        (editable ? <NfseFormCard f={f} set={set} errors={errors} /> : d && <NfseReadOnly d={d} />)}
      {d && (
        <div className="box card p-3 mb-3">
          <h2 className="h6">Valores</h2>
          <dl className="row mb-0" data-testid="nfse-values">
            <Info label="Valor dos serviços">{formatBRL(d.servicesCents)}</Info>
            <Info label="Base de cálculo">{formatBRL(d.values.baseCents)}</Info>
            <Info label="ISS">
              {formatBRL(d.values.issCents)}
              {d.issWithheld && " (retido pelo tomador)"}
            </Info>
            <Info label="Retenções federais">{formatBRL(d.values.retentionsCents)}</Info>
            <Info label="Valor líquido">{formatBRL(d.values.netCents)}</Info>
          </dl>
        </div>
      )}
      {d && d.events.length > 0 && (
        <div className="box card mb-3">
          <h2 className="h6 p-3 pb-0">Cancelamento</h2>
          <ul className="list-group list-group-flush">
            {d.events.map((e) => (
              <li key={e.id} className="list-group-item">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <strong>{NFSE_EVENT_STATUS_LABEL[e.status]}</strong>
                  <span className="text-muted small">{isoDateTimeBR(e.createdAt)}</span>
                  {e.canRefresh && canUpdate && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary ms-auto"
                      disabled={retryEvent.isPending}
                      onClick={() => retryEvent.mutate(e.id)}
                    >
                      Consultar de novo
                    </button>
                  )}
                </div>
                <div className="small">Motivo: {e.reason}</div>
                {e.statusMessage && (
                  <div className="small text-muted">{nfseMessage(e.statusCode, e.statusMessage)}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Modal
        open={canceling}
        title={d ? `Cancelar ${nfseTitle(d)}` : "Cancelar NFS-e"}
        onClose={() => setCanceling(false)}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setCanceling(false)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={cancel.isPending || reason.trim().length < 15}
              onClick={() => cancel.mutate()}
            >
              Cancelar NFS-e
            </button>
          </>
        }
      >
        <p>
          O cancelamento é um evento enviado à prefeitura; a nota autorizada não muda. A conta a receber da
          cobrança continua: estorne-a no financeiro, se for o caso.
        </p>
        <Field label="Motivo" required className="col-12" hint="De 15 a 255 caracteres">
          <textarea
            rows={3}
            value={reason}
            maxLength={255}
            data-autofocus
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <Field
          label="Código do cancelamento"
          className="col-md-6"
          hint="Só se a prefeitura exigir (ex.: 1, 2)"
        >
          <input value={code} maxLength={4} onChange={(e) => setCode(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}

function NfseFormCard({
  f,
  set,
  errors,
}: {
  f: NfseForm;
  set: <K extends keyof NfseForm>(k: K, v: NfseForm[K]) => void;
  errors: Record<string, string>;
}) {
  return (
    <>
      <div className="box card p-3 mb-3">
        <ContactPicker
          label="Tomador do serviço"
          inputId="nfse-contact"
          value={f.contact}
          error={errors.contactId}
          onChange={(v) => set("contact", v)}
        />
        <ServiceFields value={f.service} errors={errors} onChange={(v) => set("service", v)} />
      </div>
      <div className="box card p-3 mb-3">
        <h2 className="h6">Valores e tributos</h2>
        <div className="row">
          <Field label="Valor dos serviços" required className="col-md-3" error={errors.servicesCents}>
            <MoneyInput value={f.servicesCents} onChange={(v) => set("servicesCents", v)} />
          </Field>
          <Field label="Desconto incondicionado" className="col-md-3" error={errors.discountCents}>
            <MoneyInput value={f.discountCents} onChange={(v) => set("discountCents", v)} />
          </Field>
          <Field label="Deduções" className="col-md-3" error={errors.deductionsCents}>
            <MoneyInput value={f.deductionsCents} onChange={(v) => set("deductionsCents", v)} />
          </Field>
          <Field
            label="Alíquota do ISS"
            className="col-md-3"
            error={errors.issPct4}
            hint="Em branco = a da configuração"
          >
            <PercentInput value={f.issPct4} onChange={(v) => set("issPct4", v)} />
          </Field>
          <Field label="Natureza" className="col-md-4" error={errors.nature}>
            <select
              className="form-select"
              value={f.nature}
              onChange={(e) => set("nature", Number(e.target.value) as NfseForm["nature"])}
            >
              {([1, 2, 3, 4] as const).map((n) => (
                <option key={n} value={n}>
                  {NATURE_LABEL[n]}
                </option>
              ))}
            </select>
          </Field>
          {f.nature === 2 && (
            <Field label="Tipo da imunidade" required className="col-md-4" error={errors.immunityType}>
              <select
                className="form-select"
                value={f.immunityType}
                onChange={(e) => set("immunityType", e.target.value)}
              >
                <option value="">Selecione</option>
                <option value="0">Imunidade (não especificada)</option>
                <option value="1">Patrimônio, renda ou serviços de entes públicos</option>
                <option value="2">Templos de qualquer culto</option>
                <option value="3">Partidos, sindicatos, entidades sem fins lucrativos</option>
                <option value="4">Livros, jornais, periódicos e papel</option>
                <option value="5">Fonogramas e videofonogramas musicais</option>
              </select>
            </Field>
          )}
          {f.nature === 3 && (
            <Field label="País do resultado" required className="col-md-2" error={errors.exportCountry}>
              <input
                value={f.exportCountry}
                maxLength={2}
                onChange={(e) => set("exportCountry", e.target.value.toUpperCase())}
              />
            </Field>
          )}
          <div className="col-md-4 mb-3 d-flex align-items-end">
            <div className="form-check">
              <input
                id="nfse-iss-withheld"
                type="checkbox"
                className="form-check-input"
                checked={f.issWithheld}
                onChange={(e) => set("issWithheld", e.target.checked)}
              />
              <label htmlFor="nfse-iss-withheld" className="form-check-label">
                ISS retido pelo tomador
              </label>
            </div>
          </div>
        </div>
        <div className="row">
          {(
            [
              ["pisPct4", "PIS retido"],
              ["cofinsPct4", "COFINS retida"],
              ["csllPct4", "CSLL retida"],
              ["irPct4", "IR retido"],
              ["inssPct4", "INSS retido"],
            ] as const
          ).map(([k, label]) => (
            <Field key={k} label={label} className="col-6 col-md" error={errors[`retentions.${k}`]}>
              <PercentInput value={f[k]} onChange={(v) => set(k, v)} />
            </Field>
          ))}
        </div>
        <Field label="Informações complementares" className="col-12" error={errors.complementaryInfo}>
          <textarea
            rows={2}
            value={f.complementaryInfo}
            maxLength={2000}
            onChange={(e) => set("complementaryInfo", e.target.value)}
          />
        </Field>
      </div>
    </>
  );
}

function NfseReadOnly({ d }: { d: ServiceInvoiceView }) {
  const r = d.retentions;
  return (
    <div className="box card p-3 mb-3">
      <dl className="row mb-0">
        <Info label="Tomador">
          {d.recipient.name}
          {d.recipient.document && (
            <span className="text-muted ms-2">{formatDocument(d.recipient.document)}</span>
          )}
        </Info>
        <Info label="Código de tributação nacional">{d.service.cTribNac}</Info>
        {d.service.cTribMun && <Info label="Código municipal">{d.service.cTribMun}</Info>}
        <Info label="Discriminação">{d.service.description}</Info>
        <Info label="Natureza">{NATURE_LABEL[d.nature]}</Info>
        <Info label="Alíquota do ISS">{d.issPct4 === null ? null : formatPct(d.issPct4)}</Info>
        {r.pisPct4 + r.cofinsPct4 + r.csllPct4 + r.irPct4 + r.inssPct4 > 0 && (
          <Info label="Retenções">
            PIS {formatPct(r.pisPct4)} · COFINS {formatPct(r.cofinsPct4)} · CSLL {formatPct(r.csllPct4)} · IR{" "}
            {formatPct(r.irPct4)} · INSS {formatPct(r.inssPct4)}
          </Info>
        )}
        <Info label="Informações complementares">{d.complementaryInfo}</Info>
      </dl>
    </div>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 text-muted fw-normal">{label}</dt>
      <dd className="col-sm-8">{children || "—"}</dd>
    </>
  );
}
